import React from 'react';
import DataTable from 'aldoo-ra/DataTable';
import Typography from 'aldoo-ra/Typography';
import Tabs from 'aldoo-ra/Tabs';
import ActionRow from './ActionRow';
import formatDuration from '../utils/formatDuration';
import { getStatusColor } from '../utils/taskConstants';

/**
 * Task Details Panel - displays task information in tabs
 */
export default function TaskDetailsPanel({ task, activeTab, onTabChange, serverTimeOffset }) {
    if (!task) return null;

    // Calculate completion time
    const completionTime = task.completedAt && task.startTime
        ? task.completedAt - task.startTime
        : null;

    // Prepare actions data
    const actionsData = (task.actions || []).map((action, index) => ({
        index,
        name: typeof action === 'string' ? action : action?.actionName || action?.name || `Action ${index + 1}`,
        fullAction: action,
        isActive: index === task.actions.length - 1 && task.status === 'running'
    }));

    const tabs = [
        {
            value: 'details',
            label: 'Details'
        },
        {
            value: 'timing',
            label: 'Timing'
        },
        {
            value: 'actions',
            label: 'Actions'
        },
        {
            value: 'fulldata',
            label: 'Full Data'
        }
    ];

    return (
        <div className="p-6 space-y-4">
            <Tabs
                variant="admin"
                value={activeTab}
                onChange={onTabChange}
                tabs={tabs}
            />

            {activeTab === 'details' && (
                <div className="space-y-3">
                    <div>
                        <Typography variant="admin_txt2_dim" className="mb-1">Task ID</Typography>
                        <Typography variant="admin_body" className="font-mono text-sm break-all">{task.id}</Typography>
                    </div>

                    <div>
                        <Typography variant="admin_txt2_dim" className="mb-1">Name</Typography>
                        <Typography variant="admin_body">{task.name || 'Unnamed Task'}</Typography>
                    </div>

                    <div>
                        <Typography variant="admin_txt2_dim" className="mb-1">Status</Typography>
                        <span className={`px-2 py-1 rounded text-xs font-semibold uppercase ${getStatusColor(task.status)}`}>
                            {task.status}
                        </span>
                    </div>

                    {task.total && (
                        <div>
                            <Typography variant="admin_txt2_dim" className="mb-1">Progress</Typography>
                            <div className="flex items-center gap-3">
                                <div className="flex-1 h-2 bg-admin_neutral_1 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-admin_info transition-all duration-300"
                                        style={{ width: `${Math.round((task.progress / task.total) * 100)}%` }}
                                    ></div>
                                </div>
                                <Typography variant="admin_body" className="text-sm">
                                    {task.progress} / {task.total}
                                </Typography>
                            </div>
                        </div>
                    )}

                    {task.actions && task.currentActionIndex !== undefined && (
                        <div>
                            <Typography variant="admin_txt2_dim" className="mb-1">Current Action</Typography>
                            <Typography variant="admin_body">
                                {(() => {
                                    const action = task.actions[task.currentActionIndex];
                                    if (typeof action === 'string') return action;
                                    if (action?.actionName) return action.actionName;
                                    if (action?.name) return action.name;
                                    return `Step ${task.currentActionIndex + 1}`;
                                })()}
                            </Typography>
                        </div>
                    )}

                    {task.suspended !== undefined && (
                        <div>
                            <Typography variant="admin_txt2_dim" className="mb-1">State</Typography>
                            {task.suspended ? (
                                <span className="text-xs border border-admin_warning bg-admin_warning/20 text-admin_warning px-2 py-1 rounded">Suspended</span>
                            ) : (
                                <span className="text-xs border border-admin_success bg-admin_success/20 text-admin_success px-2 py-1 rounded">Active</span>
                            )}
                        </div>
                    )}

                    {task.result && (
                        <div>
                            <Typography variant="admin_txt2_dim" className="mb-2">Result</Typography>
                            <pre className="bg-admin_canvas p-3 rounded text-xs overflow-auto max-h-64 text-admin_text font-mono">
{JSON.stringify(task.result, null, 2)}
                            </pre>
                        </div>
                    )}

                    {task.error && (
                        <div>
                            <Typography variant="admin_txt2_dim" className="mb-2">Error</Typography>
                            <pre className="bg-admin_error/10 border border-admin_error/30 p-3 rounded text-xs overflow-auto max-h-64 text-admin_error font-mono">
{typeof task.error === 'string' ? task.error : JSON.stringify(task.error, null, 2)}
                            </pre>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'timing' && (
                <div className="space-y-3">
                    {task.startTime && (
                        <div>
                            <Typography variant="admin_txt2_dim" className="mb-1">Start Time (UTC)</Typography>
                            <Typography variant="admin_body" className="text-sm">
                                {new Date(task.startTime).toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC')}
                            </Typography>
                        </div>
                    )}

                    {task.completedAt && (
                        <div>
                            <Typography variant="admin_txt2_dim" className="mb-1">Completed At (UTC)</Typography>
                            <Typography variant="admin_body" className="text-sm">
                                {new Date(task.completedAt).toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC')}
                            </Typography>
                        </div>
                    )}

                    {completionTime && (
                        <div>
                            <Typography variant="admin_txt2_dim" className="mb-1">Completion Time</Typography>
                            <Typography variant="admin_body" className="text-sm">
                                {formatDuration(completionTime)}
                            </Typography>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'actions' && (
                actionsData.length > 0 ? (
                    <div className="overflow-visible">
                        <DataTable
                            localUserPreferencesKey="admin.taskManager.details"
                            variant="admin"
                            data={actionsData}
                            showFooter={false}
                            renders={[
                                {
                                    title: "#",
                                    id: "index",
                                    render: (_, row) => (
                                        <span className={`text-xs ${row.isActive ? 'text-admin_info font-semibold' : 'text-admin_text_secondary'}`}>
                                            {row.index + 1}
                                        </span>
                                    )
                                },
                                {
                                    title: "Action Name",
                                    id: "name",
                                    render: (_, row) => <ActionRow row={row} />
                                },
                                {
                                    title: "Timing",
                                    id: "timing",
                                    render: (_, row) => {
                                        const timing = task.actionTimings?.[row.index];
                                        if (!timing || timing.duration === null || timing.duration === undefined) {
                                            return <span className="text-xs text-admin_text_secondary">-</span>;
                                        }
                                        return <span className="text-xs text-admin_text">{formatDuration(timing.duration)}</span>;
                                    }
                                }
                            ]}
                            styleOverrides={{
                                table: "min-w-full",
                                headerCell: "px-3 py-2 text-left text-xs font-medium text-admin_text_secondary uppercase tracking-wider",
                                bodyCell: "px-3 py-2 text-sm relative"
                            }}
                        />
                    </div>
                ) : (
                    <Typography variant="admin_body" className="text-admin_text_secondary">No actions available</Typography>
                )
            )}

            {activeTab === 'fulldata' && (
                <pre className="bg-admin_canvas p-3 rounded text-xs overflow-auto max-h-[600px] text-admin_text_secondary font-mono">
{JSON.stringify(task, null, 2)}
                </pre>
            )}
        </div>
    );
}
